// =============================================================================
// React Hooks — Context-based ApiClient access for React apps
// =============================================================================

import { createContext, useContext } from 'react';
import type { ApiClient } from './client';

/**
 * React context for providing the ApiClient to the component tree.
 * This avoids passing the client as a prop and enables clean hook-based access.
 */
export const ApiClientContext = createContext<ApiClient | null>(null);

/**
 * Hook to access the ApiClient from the nearest ApiClientProvider.
 *
 * @throws If used outside of an ApiClientProvider.
 *
 * @example
 * ```tsx
 * function OrderList() {
 *   const api = useApiClient();
 *   const { data } = useQuery({
 *     queryKey: ['orders'],
 *     queryFn: () => api.get('/orders'),
 *   });
 *   return <div>{JSON.stringify(data)}</div>;
 * }
 * ```
 */
export function useApiClient(): ApiClient {
  const client = useContext(ApiClientContext);
  if (!client) {
    throw new Error(
      'useApiClient must be used within an ApiClientProvider. ' +
        'Wrap your app with <ApiClientProvider client={apiClient}>.',
    );
  }
  return client;
}
